import * as Sentry from '@sentry/browser';
import { navigate } from 'gatsby';
import isString from 'lodash/isString';
import React from 'react';

import { GatsbyContentfulImage } from '@/components/GatsbyContentfulImage';
import { Money } from '@/components/Money';
import { Pill } from '@/components/Pill';
import { CommandItem } from '@/components/ui/command';
import { VariantsHitsEntity } from '@/hooks/useAutocomplete';

import { AirtableStatus } from '../../constants';

const CommandItemComponent: React.FC<{
  variant: VariantsHitsEntity;
  toggle: () => void;
  recent: { query: string; slug: string; time: string }[];
  setRecent: React.Dispatch<
    React.SetStateAction<
      {
        query: string;
        slug: string;
        time: string;
      }[]
    >
  >;
  setValue: React.Dispatch<React.SetStateAction<string>>;
}> = ({ variant, toggle, recent, setRecent, setValue }) => {
  const isOnSale = variant.document.compareAtPrice > variant.document.price;
  const isPreOrder = variant.document.status === AirtableStatus.PRE_ORDER;
  const isSample = variant.document.slug.startsWith('/samples');
  const isSoldOut =
    variant.document.inStock === false &&
    variant.document.status !== AirtableStatus.ACTIVE &&
    variant.document.status !== AirtableStatus.PRE_ORDER;

  const onSelect = async () => {
    try {
      await navigate(variant.document.slug);
      toggle();

      // Only call setRecent if the item doesn't already exist
      if (!recent.some((item) => item.slug === variant.document.slug)) {
        setRecent((prev) => {
          return (
            [
              ...prev,
              {
                query: `${variant.document.productName}, ${variant.document.variantName}`,
                slug: variant.document.slug,
                time: new Date().getTime().toString(),
              },
            ]
              // Sort the array in descending order of time
              .sort((a, b) => parseInt(b.time) - parseInt(a.time))
              // Return the first 5 items
              .slice(0, 5)
          );
        });
      }

      setValue('');
    } catch (error) {
      console.error('Navigation failed:', error);
      Sentry.captureException(error);
    }
  };

  return (
    <CommandItem
      key={variant.document.id}
      value={variant.document.sku}
      className="flex items-start gap-4 hover:cursor-pointer [&_mark]:bg-red-matte/60 [&_mark]:font-bold"
      onSelect={onSelect}
    >
      <GatsbyContentfulImage
        imageUrl={variant.document.imageUrl}
        alt={`${variant.document.productName}, ${variant.document.variantName}`}
        size={64}
        className="h-16 w-16 rounded-sm mix-blend-multiply"
      />
      <div className="flex-1">
        {isString(variant.highlight?.productName?.snippet) ? (
          <div
            className="line-clamp-1 text-base font-bold text-black"
            dangerouslySetInnerHTML={{
              __html: variant.highlight.productName.snippet,
            }}
          />
        ) : (
          <div className="line-clamp-1 text-base font-bold text-black">
            {variant.document.productName}
          </div>
        )}

        {isString(variant.highlight?.variantName?.snippet) ? (
          <div
            className="mb-1 line-clamp-1 text-base text-black"
            dangerouslySetInnerHTML={{
              __html: variant.highlight.variantName.snippet,
            }}
          />
        ) : (
          <div className="mb-1 line-clamp-1 text-base text-black">
            {variant.document.variantName}
          </div>
        )}

        <div className="line-clamp-1 text-base text-black-600">
          Art. no.{' '}
          {isString(variant.highlight?.sku?.snippet) ? (
            <span
              dangerouslySetInnerHTML={{
                __html: variant.highlight.sku.snippet,
              }}
            />
          ) : (
            <span>{variant.document.sku}</span>
          )}
        </div>
      </div>
      <div className="flex flex-col content-end text-base font-bold">
        {isSoldOut ? (
          <>Sold out</>
        ) : isPreOrder ? (
          <>
            <div className="text-right">
              <span className="mr-2 text-sm font-normal text-black-600">
                List price
              </span>
              <Money
                amount={variant.document.price}
                withDiscount={false}
                sku={variant.document.sku}
              />
            </div>
            <Pill content="Coming soon" color="hem-pro" />
          </>
        ) : (
          <div className="text-right">
            {isOnSale && !isSample && (
              <div className="block font-normal text-black-600 line-through">
                <Money
                  amount={variant.document.compareAtPrice}
                  sku={variant.document.sku}
                />
              </div>
            )}

            <Money amount={variant.document.price} sku={variant.document.sku} />
          </div>
        )}

        {variant.document.type === 'custom' ? (
          <Pill content="Graded-in" color="gray" />
        ) : null}
      </div>
    </CommandItem>
  );
};

export default CommandItemComponent;
